import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Toolbar,
  Typography,
  Button,
  Container,
  Snackbar,
  Alert,
  AlertProps
} from '@mui/material';
import {
  ExitToApp as LogoutIcon,
  Poll as PollIcon
} from '@mui/icons-material';
import { useAuth } from '../services/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const { user, logout } = useAuth();
  const [feedback, setFeedback] = React.useState<{ type: AlertProps['severity']; message: string } | null>(null);

  const handleLogout = () => {
    logout();
    setFeedback({ type: 'success', message: 'Erfolgreich ausgeloggt' });
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <PollIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary', fontWeight: 600 }}>
              Mahbis Wettlokal
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {user && (
                <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary' }}>
                  Angemeldet als <strong>{user.username}</strong>
                </Typography>
              )}
              {user?.isAdmin && (
                <>
                  <Button size="small" onClick={() => navigate('/')} disabled={path === '/'}>Start</Button>
                  <Button size="small" onClick={() => navigate('/admin')} disabled={path === '/admin'}>Benutzer</Button>
                  <Button size="small" onClick={() => navigate('/admin/ideen')} disabled={path === '/admin/ideen'}>Ideen</Button>
                </>
              )}
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                Logout
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 4,
          backgroundColor: 'background.default',
        }}
      >
        {children}
      </Box>

      {feedback && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setFeedback(null)}
            severity={feedback.type}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {feedback.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Layout;
